<template>
    <v-dialog v-model="visible" max-width="600px" persistent content-class="rec-dialog">
      <v-card class="rec-card" dark>
        <div class="rec-frame-tl"></div>
        <div class="rec-frame-br"></div>

        <v-card-title class="rec-card-title">{{ isTravelPlan ? 'Edit Travel Plan' : 'Edit Recommendation' }}</v-card-title>
        <v-card-text>
          <div class="edit-row">
            <v-text-field label="City" v-model="editedCity" outlined dense dark color="amber" />
            <v-text-field
              v-if="isTravelPlan"
              label="Dates (optional)"
              v-model="editedTravelDates"
              outlined dense dark color="amber"
            />
            <v-text-field v-else label="Business name" v-model="editedBusinessName" outlined dense dark color="amber" />
          </div>
          <v-text-field
            v-if="isTravelPlan"
            type="datetime-local"
            label="Disappears at (optional)"
            v-model="editedTravelEndDate"
            hint="If set, this post vanishes automatically at this exact date and time. If left blank, it's hidden automatically 7 days after posting instead."
            persistent-hint
            outlined dense dark color="amber"
          />
          <v-select
            v-if="!isTravelPlan"
            label="Category"
            v-model="editedCategory"
            :items="categories"
            outlined dense dark color="amber" item-color="amber"
          />
          <v-textarea
            :label="isTravelPlan ? 'What are you looking for?' : 'Your recommendation'"
            v-model="editedText"
            auto-grow
            outlined
            dark
            color="amber"
          />
          <div
            v-if="!isTravelPlan"
            class="arrangement-box"
            :class="{ active: editedWantsArrangement }"
            @click="editedWantsArrangement = !editedWantsArrangement"
          >
            <v-checkbox
              v-model="editedWantsArrangement"
              hide-details
              dark
              color="amber"
              @click.stop
            />
            <div>
              <p class="arrangement-title">I'd love an official concierge arrangement here</p>
            </div>
          </div>

          <div class="media-field">
            <label class="media-field-label">Photos &amp; Videos</label>

            <div v-if="editMediaPreviews.length" class="media-grid">
              <div v-for="(item, idx) in editMediaPreviews" :key="idx" class="media-thumb">
                <img v-if="item.type === 'image'" :src="item.url" />
                <video v-else :src="item.url" controls></video>
                <button class="media-remove-btn" @click="removeEditMediaItem(idx)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>

            <button class="add-media-btn" @click="fileInput.click()">+ Add Photos/Videos</button>
            <input
              ref="fileInput"
              type="file"
              style="display: none;"
              accept="image/*,video/*"
              multiple
              @change="handleEditFileChange"
            />
          </div>

          <v-switch
            label="Also show in Explore's main feed"
            v-model="editedAlsoShareToExplore"
            hide-details
            dark
            color="amber"
            class="mt-2"
          />
          <p class="visibility-note">
            {{ editedAlsoShareToExplore
              ? 'This appears in the app-wide Explore feed, not just its chapter.'
              : `Off keeps this in the ${editedCity || 'chapter'} chapter only.` }}
          </p>
        </v-card-text>
        <v-card-actions class="rec-actions">
          <button class="rec-btn-primary" :disabled="isSavingEdit" @click="savePost">
            <div class="rec-btn-tick"></div>
            <span>{{ isSavingEdit ? 'Saving…' : 'Save Changes' }}</span>
            <div class="rec-btn-tick"></div>
          </button>
          <button class="rec-btn-text" @click="$emit('update:modelValue', false)">Cancel</button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
  
  <script setup>
  import { ref, computed, watch } from 'vue'
  import { db } from '@/firebase'
  import { doc, updateDoc, Timestamp } from 'firebase/firestore'
  import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
  
  const props = defineProps({
    modelValue: Boolean,
    post: Object,
  })
  const emit = defineEmits(['update:modelValue', 'post-updated'])

  const categories = [
    'Breakfast',
    'Lunch',
    'Dinner',
    'Late Night',
    'Coffee & Drinks',
    'Hotel & Stay',
    'Experience & Culture',
    'Wellness',
    'Other',
  ]

  const isTravelPlan = computed(() => props.post?.postType === 'travel_plan')

  const visible = ref(props.modelValue)
  const editedText = ref(props.post?.text || '')
  const editedCity = ref(props.post?.city || '')
  const editedBusinessName = ref(props.post?.businessName || '')
  const editedCategory = ref(props.post?.category || 'Dinner')
  const editedWantsArrangement = ref(props.post?.wantsArrangement || false)
  const editedTravelDates = ref(props.post?.travelDates || '')
  const editedTravelEndDate = ref(toDatetimeLocalString(props.post?.travelEndDate))
  // Lets a post created chapter-only (groupOnly = true, via
  // PostCreateDialog's "Also show in Explore's main feed" toggle left off)
  // be opted into the app-wide feed later without deleting and reposting
  // it — the inverse framing matches PostCreateDialog's own toggle.
  const editedAlsoShareToExplore = ref(!(props.post?.groupOnly))

  // Converts a stored travelEndDate (Firestore Timestamp, a plain Date,
  // or a legacy string) into the exact "YYYY-MM-DDTHH:mm" format a
  // datetime-local input requires, in local time.
  function toDatetimeLocalString(value) {
    if (!value) return ''
    const d = value?.toDate ? value.toDate() : new Date(value)
    if (isNaN(d.getTime())) return ''
    const pad = n => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const fileInput = ref(null)
  const editMediaFiles = ref([]) // newly-added Files pending upload
  const editMediaPreviews = ref([]) // [{ url, type, isNew }] — existing (remote) + newly added (local blob)
  const isSavingEdit = ref(false)

  const getMedia = (post) => {
    if (!post) return []
    if (Array.isArray(post.media) && post.media.length) return post.media
    const legacy = []
    if (post.imageURL) legacy.push({ url: post.imageURL, type: 'image' })
    if (post.videoURL) legacy.push({ url: post.videoURL, type: 'video' })
    return legacy
  }

  const resyncFromPost = (postValue) => {
    editedText.value = postValue?.text || ''
    editedCity.value = postValue?.city || ''
    editedBusinessName.value = postValue?.businessName || ''
    editedCategory.value = postValue?.category || 'Dinner'
    editedWantsArrangement.value = postValue?.wantsArrangement || false
    editedTravelDates.value = postValue?.travelDates || ''
    editedTravelEndDate.value = toDatetimeLocalString(postValue?.travelEndDate)
    editedAlsoShareToExplore.value = !(postValue?.groupOnly)
    editMediaFiles.value = []
    editMediaPreviews.value = getMedia(postValue).map(item => ({ ...item, isNew: false }))
  }

  // Populate immediately for the post passed in on initial open
  resyncFromPost(props.post)

  watch(() => props.modelValue, val => (visible.value = val))
  watch(visible, val => emit('update:modelValue', val))

  // Re-sync fields whenever a different post is opened for editing
  watch(() => props.post, (newPost) => resyncFromPost(newPost))

  const handleEditFileChange = (event) => {
    const files = Array.from(event.target.files || [])
    files.forEach(file => {
      editMediaFiles.value.push(file)
      editMediaPreviews.value.push({
        url: URL.createObjectURL(file),
        type: file.type.startsWith('image') ? 'image' : 'video',
        isNew: true,
      })
    })
    event.target.value = ''
  }

  const removeEditMediaItem = (idx) => {
    const item = editMediaPreviews.value[idx]
    if (item.isNew) {
      const newIdxBefore = editMediaPreviews.value.slice(0, idx).filter(p => p.isNew).length
      editMediaFiles.value.splice(newIdxBefore, 1)
    }
    editMediaPreviews.value.splice(idx, 1)
  }

  const savePost = async () => {
    if (!editedCity.value.trim() || isSavingEdit.value) return
    if (!isTravelPlan.value && !editedBusinessName.value.trim()) return

    isSavingEdit.value = true
    try {
      // Upload any newly-added files
      const storage = getStorage()
      const uploaded = await Promise.all(
        editMediaFiles.value.map(async (file, idx) => {
          const isImage = file.type.startsWith('image')
          const ext = isImage ? 'jpg' : 'mp4'
          const fileRef = storageRef(storage, `explore/${props.post.id}_edit_${idx}_${Date.now()}.${ext}`)
          await uploadBytes(fileRef, file)
          const url = await getDownloadURL(fileRef)
          return { url, type: isImage ? 'image' : 'video' }
        })
      )

      // Keep existing (non-removed) remote items, in order, plus newly uploaded ones
      const kept = editMediaPreviews.value.filter(p => !p.isNew).map(({ url, type }) => ({ url, type }))
      const media = [...kept, ...uploaded]

      const baseUpdates = isTravelPlan.value
        ? {
            text: editedText.value.trim(),
            city: editedCity.value.trim(),
            travelDates: editedTravelDates.value.trim(),
            travelEndDate: editedTravelEndDate.value ? Timestamp.fromDate(new Date(editedTravelEndDate.value)) : null,
          }
        : {
            text: editedText.value.trim(),
            city: editedCity.value.trim(),
            businessName: editedBusinessName.value.trim(),
            category: editedCategory.value,
            wantsArrangement: editedWantsArrangement.value,
          }

      await updateDoc(doc(db, 'explore', props.post.id), {
        ...baseUpdates,
        media,
        groupOnly: !editedAlsoShareToExplore.value,
        imageURL: null, // clear legacy single-media fields now that `media` is authoritative
        videoURL: null,
      })

      emit('post-updated')
      visible.value = false
    } catch (err) {
      console.error('Failed to save post edit:', err)
    } finally {
      isSavingEdit.value = false
    }
  }
  </script>

  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

  :deep(.rec-dialog) { box-shadow: none; }
  .rec-card {
    font-family: 'Montserrat', sans-serif !important;
    background: #0d0c0a !important;
    border: 1px solid rgba(210,175,100,0.22) !important;
    position: relative;
    overflow: visible !important;
  }
  .rec-frame-tl, .rec-frame-br { position: absolute; width: 16px; height: 16px; pointer-events: none; z-index: 2; }
  .rec-frame-tl { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.6); border-left: 1px solid rgba(210,175,100,0.6); }
  .rec-frame-br { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.6); border-right: 1px solid rgba(210,175,100,0.6); }

  .rec-card-title {
    font-family: 'Cormorant', serif !important; font-size: 28px !important; font-weight: 400 !important;
    color: #edeae2 !important;
    padding: 32px 32px 4px !important;
  }
  :deep(.v-card__text) { padding: 20px 32px 4px !important; }

  .edit-row { display: flex; gap: 12px; }
  .edit-row > * { flex: 1; }

  :deep(.v-text-field .v-label),
  :deep(.v-textarea .v-label),
  :deep(.v-select .v-label) { color: rgba(237,232,223,0.35) !important; font-family: 'Montserrat', sans-serif; font-size: 13px; }
  :deep(.v-text-field input),
  :deep(.v-textarea textarea),
  :deep(.v-select .v-select__selection) { color: #edeae2 !important; font-family: 'Montserrat', sans-serif; }
  :deep(.v-input--is-focused .v-label) { color: rgba(210,175,100,0.8) !important; }
  :deep(fieldset) { border-color: rgba(210,175,100,0.22) !important; border-radius: 2px !important; }
  :deep(.v-input--is-focused fieldset) { border-color: rgba(210,175,100,0.6) !important; }
  :deep(.v-icon) { color: rgba(210,175,100,0.45) !important; }
  :deep(.v-menu__content) { background: #0f0e0c !important; border: 1px solid rgba(210,175,100,0.2) !important; }
  :deep(.v-list-item__title) { color: rgba(237,232,223,0.7) !important; font-family: 'Montserrat', sans-serif; }
  :deep(.v-list-item--active .v-list-item__title) { color: rgba(210,175,100,0.9) !important; }
  :deep(.v-list) { background: #0f0e0c !important; }
  :deep(.v-input--checkbox .v-label) { color: rgba(237,232,223,0.55) !important; font-size: 13px; }
  :deep(.v-input--switch .v-label) { color: rgba(237,232,223,0.55) !important; font-size: 13px; }

  .visibility-note { font-size: 11px; color: rgba(237,232,223,0.3); margin: -8px 0 8px 2px; }

  .arrangement-box {
    display: flex; align-items: flex-start; gap: 4px;
    border: 1px solid rgba(210,175,100,0.28);
    border-left: 3px solid rgba(210,175,100,0.28);
    background: rgba(210,175,100,0.05);
    border-radius: 2px;
    padding: 8px 16px 8px 4px;
    margin: 6px 0 8px;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
  }
  .arrangement-box.active {
    background: rgba(210,175,100,0.16);
    border-color: rgba(210,175,100,0.7);
    border-left-color: #d2af64;
  }
  .arrangement-title { font-size: 13px; font-weight: 600; color: #edeae2; }
  .arrangement-box.active .arrangement-title { color: #d2af64; font-weight: 700; }

  .media-field { margin-top: 6px; }
  .media-field-label { display: block; font-size: 9px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,175,100,0.45); margin-bottom: 10px; }
  .media-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
  .media-thumb { position: relative; aspect-ratio: 1; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; }
  .media-thumb img, .media-thumb video { width: 100%; height: 100%; object-fit: cover; display: block; }
  .media-remove-btn { position: absolute; top: 4px; right: 4px; width: 20px; height: 20px; border-radius: 50%; background: rgba(0,0,0,0.7); border: 1px solid rgba(210,175,100,0.3); color: rgba(237,232,223,0.8); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s, color 0.2s; }
  .media-remove-btn:hover { background: rgba(200,60,60,0.7); color: #fff; }
  .media-remove-btn svg { width: 11px; height: 11px; }
  .add-media-btn { display: inline-block; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.6); background: transparent; border: 1px solid rgba(210,175,100,0.25); padding: 8px 14px; cursor: pointer; transition: border-color 0.2s, color 0.2s; }
  .add-media-btn:hover { border-color: rgba(210,175,100,0.55); color: rgba(210,175,100,0.9); }

  .rec-actions { padding: 12px 32px 32px !important; gap: 14px; }
  .rec-btn-primary {
    display: flex; align-items: center; gap: 12px;
    font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase;
    color: rgba(210,175,100,0.9);
    background: rgba(210,175,100,0.08);
    border: 1px solid rgba(210,175,100,0.5);
    padding: 13px 22px;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, color 0.2s;
  }
  .rec-btn-primary:hover { background: rgba(210,175,100,0.16); border-color: rgba(210,175,100,0.75); }
  .rec-btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
  .rec-btn-tick { width: 14px; height: 1px; background: rgba(210,175,100,0.4); flex-shrink: 0; }
  .rec-btn-text {
    font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase;
    color: rgba(237,232,223,0.3);
    background: none; border: none; cursor: pointer; padding: 13px 10px;
    transition: color 0.2s;
  }
  .rec-btn-text:hover { color: rgba(237,232,223,0.6); }
  </style>